/** @jsx jsx */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { jsx, css } from '@emotion/react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import Select from 'react-select';
import ReactSelect from '../../components/utilComponents/tmSlimSelect/reactSelect';
import { notify } from '../../util/notifications';
import { GOOGLE_API_KEY } from '../../util/constants';
import { isManagement } from '../../util/customMethods';
import { getInvoiceDetails, getTruckList, getAccountByInvoice } from '../../actions/dispatchAction/dispatchCallsAction';
import { assignInvoice, assignInvoiceToCompany, filterTruckList, filterInvoiceList } from '../../actions/dispatchAction/assignInvoiceAction';
import VisibilityToggles from '../../components/dispatchComponents/assign/visibilityToggles/VisibilityToggles';
import TmReactTable from '../../components/utilComponents/tmReactTable/tmReactTable';
import AssignMap from '../../components/dispatchComponents/assign/AssignMap';
import StreetView from '../../components/dispatchComponents/assign/StreetView';
import { getChildList } from '../../actions/dispatchAction/invoiceSearchAction';

// ── Styles ──────────────────────────────────────────────────────────────────

const pageHeader = css`
    margin-bottom: 12px;
    & h4 { font-size: 1.15rem; font-weight: 600; color: #2c3e50;
           display: flex; align-items: center; gap: 8px; margin: 0;
           & i { color: #5a8bb5; } }
`;

const detailGrid = css`
    dt { font-size: 0.8rem; color: #6c757d; font-weight: 600; }
    dd { font-size: 0.85rem; color: #2c3e50; margin-bottom: 6px; }
`;

const cashFlag = css`
    font-size: 0.85rem; font-weight: 700; color: #dc3545;
    padding: 2px 0; margin-bottom: 4px;
`;

const assignBtnGroup = css`
    display: flex; gap: 8px; justify-content: flex-end; margin-top: 28px;
`;

const truckTableRow = css`
    font-size: 0.82rem;
`;

const loadingOverlay = css`
    display: flex; align-items: center; justify-content: center;
    min-height: 300px; color: #6c757d; font-size: 1rem;
`;

// ── Initial filter state ────────────────────────────────────────────────────

const INITIAL_FILTERS = {
    trucks: {
        Available: { text: 'Available', selected: false },
        Assigned:  { text: 'Assigned',  selected: false },
        Loaded:    { text: 'Loaded',    selected: false },
        Doubled:   { text: 'Doubled',   selected: false },
    },
    invoice: {
        pickup: {
            Unassigned: { text: 'Unassigned', selected: false },
            Enroute:    { text: 'Enroute',    selected: false },
            Arrived:    { text: 'Arrived',    selected: false },
            Hooked:     { text: 'Hooked',     selected: false },
            Dropped:    { text: 'Dropped',    selected: false },
        },
        priority: {
            1: { text: '1', selected: false }, 2: { text: '2', selected: false },
            3: { text: '3', selected: false }, 4: { text: '4', selected: false },
            5: { text: '5', selected: false }, 6: { text: '6', selected: false },
            7: { text: '7', selected: false }, 8: { text: '8', selected: false },
        },
    },
    dropLocation: false,
};

// ── Truck table columns ─────────────────────────────────────────────────────

const TRUCK_COLUMNS = [
    { Header: 'Truck',  accessor: 'truckId',     style: { textAlign: 'center' } },
    { Header: 'Driver', accessor: 'userId',       style: { textAlign: 'center' } },
    { Header: 'Status', accessor: 'truckStatus',  style: { textAlign: 'center' } },
];

// ── Component ───────────────────────────────────────────────────────────────

const AssignPage = ({ dispatch, history, match, refNotify, invoiceRecord, truckList, associateComp }) => {
    const isMgmt = isManagement();
    const paramInvoice = match.params.invoice;

    // ── Local state ─────────────────────────────────────────────────────
    const [invoiceId] = useState(paramInvoice || '');
    const [userId, setUserId] = useState('');
    const [truckId, setTruckId] = useState('');
    const [driverId, setDriverId] = useState('');
    const [companyId, setCompanyId] = useState('');
    const [filterData, setFilterData] = useState(INITIAL_FILTERS);
    const [loading, setLoading] = useState(true);

    // ── Init ────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!invoiceId) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            await dispatch(getInvoiceDetails(invoiceId));
            if (isMgmt) {
                await dispatch(getTruckList(invoiceId));
                const accId = await dispatch(getAccountByInvoice(invoiceId));
                if (accId) dispatch(getChildList(accId));
            }
            if (!cancelled) setLoading(false);
        };
        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoiceId]);

    // ── Document title ──────────────────────────────────────────────────
    useEffect(() => { document.title = `Assign ${invoiceId}`; }, [invoiceId]);

    // ── Driver/Truck select handler ─────────────────────────────────────
    const handleDriverTruckChange = useCallback((option, key) => {
        if (!option) {
            setUserId(''); setTruckId(''); setDriverId('');
            return;
        }
        const match = truckList.find((item) => item[key] === (option.value || ''));
        if (match) {
            setUserId(match.userId);
            setTruckId(match.truckId);
            setDriverId(match.driverId);
            setCompanyId(0);
        }
    }, [truckList]);

    // ── Assign / Unassign ───────────────────────────────────────────────
    const handleAssign = useCallback(async () => {
        if (driverId && truckId) {
            const res = await dispatch(assignInvoice(invoiceId, driverId, truckId, isMgmt));
            if (res.statusCode === 200) {
                history.push('/dispatch/dashboard');
            } else {
                notify.createNotification('error', res.statusMsg, refNotify);
            }
        } else if (companyId) {
            const res = await dispatch(assignInvoiceToCompany(invoiceId, companyId, isMgmt));
            if (res.statusCode === 200) {
                notify.createNotification('success', res.statusMsg, refNotify);
                history.push('/dispatch/dashboard');
            } else {
                notify.createNotification('error', res.statusMsg, refNotify);
            }
        }
    }, [dispatch, invoiceId, driverId, truckId, companyId, isMgmt, history, refNotify]);

    const handleUnassign = useCallback(async () => {
        const res = await dispatch(assignInvoice(invoiceId, '', '', isMgmt));
        if (res.statusCode === 200) {
            history.push('/dispatch/dashboard');
        } else {
            notify.createNotification('error', res.statusMsg, refNotify);
        }
    }, [dispatch, invoiceId, isMgmt, history, refNotify]);

    // ── Filter change (immutable) ───────────────────────────────────────
    const handleFilterChange = useCallback((key, newValue) => {
        setFilterData((prev) => ({ ...prev, [key]: newValue }));
    }, []);

    // ── Derived data (memoized) ─────────────────────────────────────────
    const filteredTrucks = useMemo(
        () => filterTruckList(truckList, filterData.trucks),
        [truckList, filterData.trucks]
    );

    const filteredCalls = useMemo(
        () => filterInvoiceList(
            [invoiceRecord],
            { pickup: filterData.invoice.pickup, priority: filterData.invoice.priority }
        ),
        [invoiceRecord, filterData.invoice]
    );

    const canAssign = (driverId !== '' && truckId !== '') || (companyId !== '' && companyId !== 0);

    const driverOptions = useMemo(() => truckList.map((item) => ({
        value: item.userId,
        label: isMgmt ? `${item.userId} [${item.subdomain}]` : item.userId,
    })), [truckList, isMgmt]);

    const truckOptions = useMemo(() => truckList.map((item) => ({
        value: item.truckId,
        label: isMgmt ? `${item.truckId} [${item.subdomain}]` : item.truckId,
    })), [truckList, isMgmt]);

    const companyOptions = useMemo(() => (associateComp || []).map((item) => ({
        value: item.idCompany,
        label: item.optionText,
    })), [associateComp]);

    const companyValue = useMemo(() => {
        const found = (associateComp || []).find((e) => String(e.idCompany) === String(companyId || ''));
        return found ? { value: found.idCompany, label: found.optionText } : null;
    }, [associateComp, companyId]);

    // ── Loading ─────────────────────────────────────────────────────────
    if (loading) {
        return <div css={loadingOverlay}><i className="fa fa-spinner fa-spin mr-2" /> Loading assignment data…</div>;
    }

    // ── Render ──────────────────────────────────────────────────────────
    return (
        <>
            {/* Header */}
            <div className="page-header" css={pageHeader}>
                <div className="row mb-10">
                    <div className="col-md-12 col-sm-12">
                        <h4><i className="fa fa-id-card" /> Assign {invoiceRecord.displayInvoiceId}</h4>
                    </div>
                </div>
            </div>

            <div className="pd-10 bg-white border-radius-4 box-shadow container-box">
                {/* Visibility toggles */}
                <div className="row">
                    <div className="col-md-12">
                        <VisibilityToggles filterContent={filterData} onSelectionChange={handleFilterChange} />
                    </div>
                </div>

                {/* Selection row */}
                <div className="row">
                    {isMgmt && (
                        <div className="col-3 form-group">
                            <label>Select Company:</label>
                            <ReactSelect
                                name="idCompany"
                                closeMenuOnSelect
                                options={companyOptions}
                                value={companyValue}
                                onChange={(opt) => {
                                    setCompanyId(parseInt(opt.value, 10));
                                    setTruckId(''); setDriverId(''); setUserId('');
                                }}
                            />
                        </div>
                    )}
                    <div className="col-md-3">
                        <div className="form-group">
                            <label>Select Driver:</label>
                            <Select
                                name="drivers" isClearable
                                options={driverOptions}
                                value={userId ? { value: userId, label: userId } : null}
                                onChange={(e) => handleDriverTruckChange(e, 'userId')}
                            />
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="form-group">
                            <label>Select Truck:</label>
                            <Select
                                name="trucks" isClearable
                                options={truckOptions}
                                value={truckId ? { value: truckId, label: truckId } : null}
                                onChange={(e) => handleDriverTruckChange(e, 'truckId')}
                            />
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div css={assignBtnGroup}>
                            <button type="button" className="btn btn-sm btn-success"
                                disabled={!canAssign} onClick={handleAssign}>
                                Assign
                            </button>
                            <button type="button" className="btn btn-sm btn-danger"
                                onClick={handleUnassign}>
                                Unassign
                            </button>
                        </div>
                    </div>
                </div>

                {/* Map + Details */}
                <div className="row">
                    <div className="col-md-7">
                        <div className="card mt-3" style={{ minHeight: 500 }}>
                            <div className="card-body p-0">
                                <AssignMap
                                    mapVehicleInfo={filteredTrucks}
                                    dropPointers={filterData.dropLocation}
                                    pickUpPointers
                                    callData={filteredCalls}
                                    showRoute
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-md-5">
                        {/* Invoice detail card */}
                        <dl className="row" css={detailGrid}>
                            {(invoiceRecord.accountingType || '') !== '' && (
                                <dt className="col-sm-12" css={cashFlag}>{invoiceRecord.accountingType}</dt>
                            )}
                            <dt className="col-sm-4">Invoice</dt>
                            <dd className="col-sm-8">{invoiceRecord.invoiceId}</dd>
                            <dt className="col-sm-4">Req By</dt>
                            <dd className="col-sm-8">{invoiceRecord.accountName}</dd>
                            <dt className="col-sm-4">Reason</dt>
                            <dd className="col-sm-8">{invoiceRecord.reason}</dd>
                            <dt className="col-sm-4">Tow</dt>
                            <dd className="col-sm-8">{invoiceRecord.towType}</dd>
                            <dt className="col-sm-4">Pickup Address</dt>
                            <dd className="col-sm-8">{invoiceRecord.pickupAddress}</dd>
                            <dt className="col-sm-4">Drop Address</dt>
                            <dd className="col-sm-8">{invoiceRecord.dropoffAddress}</dd>
                        </dl>

                        {/* Street View */}
                        <div className="row">
                            <div className="col-md-12">
                                <div className="card text-center">
                                    <div className="card-body" style={{ minHeight: 250 }}>
                                        <StreetView address={invoiceRecord.pickupLatlang} APIkey={GOOGLE_API_KEY} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Truck table */}
                        <div className="row">
                            <div className="col-md-12">
                                <div className="card mt-3">
                                    <div className="card-body p-0" css={truckTableRow}>
                                        <TmReactTable
                                            filterable={false} sortable={false} resizable={false}
                                            showPagination={false}
                                            data={truckList || []}
                                            columns={TRUCK_COLUMNS}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const mapStateToProps = (state) => ({
    refNotify: state.refNotify,
    invoiceRecord: state.dispatchCalls.invoiceRecord,
    truckList: state.dispatchCalls.truckList,
    childCompList: state.searchInvoiceList.childComList,
    associateComp: state.searchInvoiceList.associateComp,
});

export default withRouter(connect(mapStateToProps)(AssignPage));
